/**
 * Revisiones del Monitor de Precios — lectura de corridas (Fase 4 visibilidad).
 * Lista las últimas revisiones y, al elegir una, muestra sus números en español:
 * respuestas en blanco de Amazon, pausados, pausados por entrega nula, reactivados,
 * congelados. Consume /api/price-monitor/runs y /run-report.
 */
import { useCallback, useEffect, useState } from 'react';
import { useUIStore } from '../../stores/uiStore';
import { api } from '../../api/client';
import { RefreshCw, Activity } from 'lucide-react';

interface RunRow {
  id: number;
  run_id: string;
  status: string;
  phase: string | null;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  total_items: number | null;
  processed_items: number | null;
  failed_count: number | null;
  /** Etiqueta del director de turnos, p.ej. "PRICE MONITOR PASADA 1 DE 4". */
  label?: string | null;
}

interface RunReport {
  run: RunRow;
  metrics: {
    amazon_reads: number;
    empty_reads: number;
    paused: number;
    paused_delivery_null: number;
    reactivated: number;
    frozen: number;
  };
}

const STATUS_LABEL: Record<string, { txt: string; cls: string }> = {
  done: { txt: 'Terminó bien', cls: 'bg-green-100 text-green-800' },
  running: { txt: 'En curso', cls: 'bg-blue-100 text-blue-800' },
  cancelled: { txt: 'Cancelada', cls: 'bg-gray-100 text-gray-700' },
  failed: { txt: 'Falló', cls: 'bg-red-100 text-red-800' },
  error: { txt: 'Error', cls: 'bg-red-100 text-red-800' },
};

function fmtDur(ms: number | null): string {
  if (!ms) return '—';
  const min = ms / 60000;
  if (min < 1) return `${Math.round(ms / 1000)}s`;
  return `${min.toFixed(1)} min`;
}
function fmtWhen(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Metric({
  label,
  value,
  warn,
  bad,
}: {
  label: string;
  value: number | string;
  warn?: boolean;
  bad?: boolean;
}) {
  const color = bad ? 'text-red-600' : warn ? 'text-yellow-600' : 'text-[var(--color-text)]';
  return (
    <div className="border border-[var(--color-border)] rounded-lg p-3">
      <div className="text-xs text-[var(--color-text-muted)]">{label}</div>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

export default function PriceMonitorRunsPage() {
  const { sellerId } = useUIStore();
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [report, setReport] = useState<RunReport | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(
    async (runId: string) => {
      setSelectedId(runId);
      try {
        const res = await api.get<RunReport>(
          `/price-monitor/run-report?seller_id=${sellerId}&run_id=${runId}`
        );
        if (res.success && res.data) setReport(res.data);
      } catch {
        /* silencioso */
      }
    },
    [sellerId]
  );

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<RunRow[]>(`/price-monitor/runs?seller_id=${sellerId}&limit=15`);
      if (res.success && res.data) {
        setRuns(res.data);
        if (res.data.length) loadReport(res.data[0].run_id);
      } else {
        setError(res.error || 'No se pudo cargar');
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [sellerId, loadReport]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const m = report?.metrics;
  const emptyPct =
    m && m.amazon_reads > 0 ? ((m.empty_reads / m.amazon_reads) * 100).toFixed(1) : '0';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2 text-[var(--color-text)]">
          <Activity size={20} /> Revisiones del Monitor de Precios
        </h1>
        <button
          onClick={loadRuns}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualizar
        </button>
      </div>

      {error && <div className="bg-red-100 text-red-800 px-3 py-2 rounded">{error}</div>}

      {m && report && (
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 shadow-sm">
          <div className="text-sm text-[var(--color-text-muted)] mb-3">
            Revisión del {fmtWhen(report.run.started_at)}
            {report.run.label ? ` · ${report.run.label}` : ''} · duró {fmtDur(report.run.duration_ms)} ·{' '}
            {STATUS_LABEL[report.run.status]?.txt || report.run.status}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Metric label="Productos revisados (Amazon)" value={m.amazon_reads} />
            <Metric
              label="Respuestas en blanco de Amazon"
              value={`${m.empty_reads} (${emptyPct}%)`}
              warn={Number(emptyPct) > 15}
            />
            <Metric label="Pausados" value={m.paused} />
            <Metric label="Pausados por entrega nula" value={m.paused_delivery_null} />
            <Metric label="Reactivados" value={m.reactivated} />
            <Metric
              label="Congelados nuevos"
              value={m.frozen}
              warn={m.frozen > 0}
              bad={m.frozen > 100}
            />
          </div>
        </div>
      )}

      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-bg)] text-[var(--color-text-muted)]">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Cuándo</th>
              <th className="text-left px-3 py-2 font-medium">Estado</th>
              <th className="text-right px-3 py-2 font-medium">Duración</th>
              <th className="text-right px-3 py-2 font-medium">Productos</th>
              <th className="text-right px-3 py-2 font-medium">Fallos</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const st = STATUS_LABEL[r.status] || {
                txt: r.status,
                cls: 'bg-gray-100 text-gray-700',
              };
              return (
                <tr
                  key={r.id}
                  onClick={() => loadReport(r.run_id)}
                  className={`border-t border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-surface-hover)] ${
                    selectedId === r.run_id ? 'bg-[var(--color-surface-hover)]' : ''
                  }`}
                >
                  <td className="px-3 py-2 text-[var(--color-text)]">
                    {fmtWhen(r.started_at)}
                    {r.label && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium bg-[var(--color-bg)] text-[var(--color-text-muted)] border border-[var(--color-border)]">
                        {r.label}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${st.cls}`}>{st.txt}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-[var(--color-text)]">
                    {fmtDur(r.duration_ms)}
                  </td>
                  <td className="px-3 py-2 text-right text-[var(--color-text)]">
                    {r.processed_items ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-[var(--color-text)]">
                    {r.failed_count ?? 0}
                  </td>
                </tr>
              );
            })}
            {!runs.length && !loading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-[var(--color-text-muted)]">
                  Sin revisiones
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
